import { Ban, AlertTriangle } from 'lucide-react';
import { ApplicationUser } from '../../../api/admin';
import { SUSPEND_REASONS } from '../constants';

interface SuspendUserModalProps {
    user: ApplicationUser;
    reason: string;
    onReasonChange: (reason: string) => void;
    detail: string;
    onDetailChange: (detail: string) => void;
    onClose: () => void;
    onConfirm: () => void;
    isPending: boolean;
}

export function SuspendUserModal({
    user,
    reason,
    onReasonChange,
    detail,
    onDetailChange,
    onClose,
    onConfirm,
    isPending,
}: SuspendUserModalProps) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 dark:bg-black/70 px-4">
            <div className="w-full max-w-lg rounded-xl bg-white dark:bg-gray-800 shadow-xl">
                <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-6 py-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                        <Ban className="w-5 h-5 text-red-500 dark:text-red-400" />
                        ระงับการใช้งานผู้ใช้
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                    >
                        ปิด
                    </button>
                </div>
                <div className="px-6 py-5 space-y-4">
                    {/* User info */}
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-4">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            <span className="font-medium">เบอร์โทร:</span> {user.phoneNumber}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            <span className="font-medium">ชื่อ:</span> {[user.firstName, user.lastName].filter(Boolean).join(' ') || 'ไม่ระบุชื่อ'}
                        </p>
                    </div>

                    {/* Suspend reason */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            เหตุผลการระงับ <span className="text-red-500 dark:text-red-400">*</span>
                        </label>
                        <select
                            value={reason}
                            onChange={event => onReasonChange(event.target.value)}
                            className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400"
                        >
                            <option value="">-- เลือกเหตุผล --</option>
                            {SUSPEND_REASONS.map(r => (
                                <option key={r} value={r}>
                                    {r}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Detail */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            รายละเอียดเพิ่มเติม (ไม่บังคับ)
                        </label>
                        <textarea
                            value={detail}
                            onChange={event => onDetailChange(event.target.value)}
                            rows={3}
                            className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400"
                            placeholder="ระบุรายละเอียดเพิ่มเติม..."
                        />
                    </div>

                    {/* Warning */}
                    <div className="rounded-lg border border-yellow-200 dark:border-yellow-900/50 bg-yellow-50 dark:bg-yellow-900/20 p-3 text-sm text-yellow-800 dark:text-yellow-300">
                        <div className="flex items-start gap-2">
                            <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-500 flex-shrink-0" />
                            <div>
                                <p className="font-medium">คำเตือน</p>
                                <p>ผู้ใช้จะไม่สามารถเข้าใช้งานแอปและสมัครใหม่ได้จนกว่าจะยกเลิกการระงับ</p>
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                            ยกเลิก
                        </button>
                        <button
                            type="button"
                            onClick={onConfirm}
                            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                            disabled={isPending || !reason}
                        >
                            {isPending ? 'กำลังดำเนินการ...' : 'ยืนยันระงับ'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
